import React, { memo, useCallback, useEffect, useMemo } from 'react';
import { StyleSheet, Text, View } from 'react-native';
import { useDispatch, useSelector } from 'react-redux';

import { ActionButtons, DataTable, Navigation, Panel, Container } from '../../components';
import { THEME } from '../../../constants/styles';
import { appointmentLabelForType } from '../scheduler/common_views';
import {
  deleteSchedulerAppointmentWindow as deleteSchedulerAppointmentWindowAction,
  getSchedulerAppointmentWindows as getSchedulerAppointmentWindowsAction,
  resetAppointmentWindowsState,
} from './slice';
import { REQUESTING } from '../../../actions/statusTypes';
import { EVENT_TYPE_READINESS } from '../../../constants/eventTypes';
import {
  getAdminSchedulerEventTypes,
  getBaseSetupSchedulerEventTypesPermissions,
} from '../../../selectors/scheduler';
import { formatNonBusinessDays } from './scheduler_appointment_window_utils';
import { equalsSet } from '../../../reducers/helpers';

const DEFAULT_SCHEDULING_WINDOW = 90;
export default memo(function SchedulerAppointmentWindows(props) {
  const employerId = props.employer?.employerId;
  const dispatch = useDispatch();
  const appointmentWindows = useSelector(
    (state) => state.screens.airforceSetup.appointmentWindowsData || []
  );
  const appointmentWindowsStatus = useSelector(
    (state) => state.screens.airforceSetup.appointmentWindowsStatus
  );
  const deleteAppointmentWindowStatus = useSelector(
    (state) => state.screens.airforceSetup.deleteAppointmentWindowStatus
  );
  const appointmentWindowsError = useSelector(
    (state) => state.screens.airforceSetup.appointmentWindowsError
  );
  const deleteAppointmentWindowError = useSelector(
    (state) => state.screens.airforceSetup.deleteAppointmentWindowError
  );

  const availableEventTypes = useSelector(getAdminSchedulerEventTypes);
  const setupPerms = useSelector(getBaseSetupSchedulerEventTypesPermissions);
  const visibleAppointmentTypes = useMemo(
    () => availableEventTypes.filter((et) => et !== EVENT_TYPE_READINESS),
    [availableEventTypes]
  );

  const defaultWindowByAppointmentType = useMemo(() => {
    return props.employer?.eventTypesConfiguration?.eventTypeSchedulingWindows || {};
  }, [props.employer?.eventTypesConfiguration?.eventTypeSchedulingWindows]);

  const isLoading = appointmentWindowsStatus === REQUESTING;
  const isMutating = deleteAppointmentWindowStatus === REQUESTING;

  useEffect(() => {
    if (!employerId || visibleAppointmentTypes.length === 0) return;

    dispatch(getSchedulerAppointmentWindowsAction({ employerId }));

    return () => {
      dispatch(resetAppointmentWindowsState());
    };
  }, [dispatch, employerId, visibleAppointmentTypes.length]);

  const rowData = useMemo(() => {
    return appointmentWindows
      .filter((item) => visibleAppointmentTypes.includes(item.appointmentType))
      .map((item) => ({
        ...item,
        key: item.employerAppointmentWindowsId || item.appointmentType,
        appointmentTypeLabel: appointmentLabelForType(item.appointmentType),
        schedulingWindowLabel: String(item.schedulingWindow ?? 0),
        cutoffBusinessDaysLabel: String(item.cutoffBusinessDays ?? 0),
        nonBusinessDaysLabel: formatNonBusinessDays(item.nonBusinessDays),
      }))
      .sort((a, b) => {
        return (
          a.appointmentTypeLabel.localeCompare(b.appointmentTypeLabel) ||
          a.appointmentType.localeCompare(b.appointmentType)
        );
      });
  }, [appointmentWindows, visibleAppointmentTypes]);

  const isDefaultWindow = useCallback(
    (item) => {
      const defaultSchedulingWindow =
        defaultWindowByAppointmentType[item.appointmentType] ?? DEFAULT_SCHEDULING_WINDOW;
      const isSameWindow = item?.schedulingWindow === defaultSchedulingWindow;
      const isSameCutoffBusinessDays = item?.cutoffBusinessDays === 0;
      const isSameNonBusinessDays = equalsSet(item?.nonBusinessDays, [6, 7]);
      return isSameWindow && isSameCutoffBusinessDays && isSameNonBusinessDays;
    },
    [defaultWindowByAppointmentType]
  );

  const canEditItem = useCallback(
    (item) => {
      const type = item?.appointmentType;
      return !!setupPerms[type]?.canEdit;
    },
    [setupPerms]
  );

  const openEditorModal = useCallback(
    (item) => {
      Navigation.showModal({
        screen: 'SchedulerAppointmentWindowForm',
        title: `Edit ${item.appointmentType} Windows`,
        passProps: {
          employer: props.employer,
          item,
        },
        options: {
          containerStyle: {
            maxWidth: 900,
          },
        },
      });
    },
    [props.employer]
  );

  const handleEditPress = useCallback(
    (item) => {
      openEditorModal(item);
    },
    [openEditorModal]
  );

  const handleDelete = useCallback(
    (item) => {
      dispatch(
        deleteSchedulerAppointmentWindowAction({
          employerId,
          employerAppointmentWindowsId: item.employerAppointmentWindowsId,
        })
      );
    },
    [dispatch, employerId]
  );

  const handleDeletePress = useCallback(
    (item) => {
      props.onConfirmationOpts({
        title: 'Delete this scheduler appointment window?',
        description: `This will reset ${item.appointmentTypeLabel} to its default scheduling window settings.`,
        confirm: 'Yes, Delete',
        onConfirmed: () => {
          handleDelete(item);
        },
      });
    },
    [handleDelete, props.onConfirmationOpts]
  );

  const columns = useMemo(() => {
    return [
      {
        headerLabel: 'APPOINTMENT TYPE',
        style: { width: 180, flexGrow: 1 },
        extraRowStyle: styles.shortRow,
        dataAccessor: 'appointmentTypeLabel',
      },
      {
        headerLabel: 'SCHEDULING WINDOW',
        style: { width: 140, flexGrow: 1 },
        extraRowStyle: styles.shortRow,
        dataAccessor: 'schedulingWindowLabel',
      },
      {
        headerLabel: 'CUTOFF WINDOW',
        style: { width: 120, flexGrow: 1 },
        extraRowStyle: styles.shortRow,
        dataAccessor: 'cutoffBusinessDaysLabel',
      },
      {
        headerLabel: 'NON BUSINESS DAYS',
        style: { width: 220, flexGrow: 2 },
        extraRowStyle: styles.shortRow,
        dataAccessor: 'nonBusinessDaysLabel',
      },
      {
        headerLabel: 'ACTIONS',
        style: { width: 100, flexGrow: 0, flexShrink: 0 },
        extraRowStyle: styles.shortRow,
        dataAccessor: (item) => (
          <ActionButtons
            item={item}
            onEditPress={handleEditPress}
            editDisabled={!canEditItem(item) || isMutating}
            onRejectPress={handleDeletePress}
            rejectDisabled={!canEditItem(item) || isDefaultWindow(item) || isMutating}
            rejectButtonTestId={
              item.employerAppointmentWindowsId
                ? `scheduler-appointment-window-delete-${item.employerAppointmentWindowsId}`
                : undefined
            }
          />
        ),
      },
    ];
  }, [canEditItem, handleDeletePress, handleEditPress, isDefaultWindow, isMutating]);

  if (!employerId || visibleAppointmentTypes.length === 0) return null;

  return (
    <Panel
      title="Scheduler Appointment Windows"
      containerStyle={[styles.panel, props.containerStyle]}
    >
      <Container
        errorMessage={
          deleteAppointmentWindowError?.message || appointmentWindowsError?.message || null
        }
        renderActivityOverlay={!!isLoading || !!isMutating}
      >
        <DataTable columns={columns} data={rowData} altColors={true} />
        {!isLoading && rowData.length === 0 && (
          <View style={styles.emptyStateContainer}>
            <Text style={THEME.textRegular}>No scheduler appointment windows are available.</Text>
          </View>
        )}
      </Container>
    </Panel>
  );
});

const styles = StyleSheet.create({
  panel: {
    minWidth: 780,
  },
  shortRow: {
    paddingVertical: 1,
  },
  emptyStateContainer: {
    paddingVertical: 25,
    paddingHorizontal: 15,
  },
});
